/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import useResponse from "@/shared/hooks/useResponse";
import { useMainBannerStore } from "@/stores/useMainBannerStore";

import { colors } from "@dotnet/telematics-design-system";

import useAxiosPrivate from "../../../../api/hooks/useAxiosPrivate";
import { ROUTES, useMediaQueries } from "../../../../shared";
import RectangleLoader from "../../../../shared/components/loaders/RectangleLoader";
import useGeneralHeights from "../../../../shared/hooks/useGeneralHeights";
import { useTableStoreByRoute } from "../../../../stores/useTableStore";
import Autocomplete from "../../../Autocomplete/Autocomplete";
import PaginationLoader from "../../../Loaders/PaginationLoader";
import Pagination from "../../../Pagination/Pagination";
import { tableFooterStyle } from "../../styles/styleFunctions";
import { useTableStyles } from "../../styles/styles";
import { OptionPerPage } from "../../types";

interface TableFooterProps {
  route: ROUTES;
}

const TableFooter = ({ route }: TableFooterProps) => {
  const { t } = useTranslation();
  const { fromLg, toLg } = useMediaQueries();
  const { handleResponse } = useResponse();
  const { getData } = useAxiosPrivate();

  const mainBannerHeight = useMainBannerStore(state => state.height);
  const { DESKTOP_TABLE_FOOTER_HEIGHT } = useGeneralHeights(mainBannerHeight);

  const { initialLoading, pagination, setParams, fetchData } =
    useTableStoreByRoute(route);

  const {
    page: currentPageNumber,
    totalCount: totalPageCount,
    pageSize
  } = pagination;

  const { tableFooter } = useTableStyles();
  const options: OptionPerPage[] = ["25", "50", "100"];

  const rowsPerPage = String(pageSize);

  const handlePageChange = async (newPage: number) => {
    setParams({ pageNumber: newPage.toString() });
    await fetchData(getData, handleResponse);
  };

  const handleRowsPerPageChange = async (value: OptionPerPage) => {
    const currentPage = pagination.page;
    const maxPageCount = Math.ceil(pagination.totalCount / +value);

    // If current page would be out of bounds with new page size, adjust it
    if (currentPage > maxPageCount) {
      setParams({
        showPerPage: value,
        pageNumber: maxPageCount.toString()
      });
    } else {
      setParams({ showPerPage: value });
    }

    await fetchData(getData, handleResponse);
  };

  const renderTableFooterContent = () => {
    if (initialLoading) {
      return (
        <>
          <RectangleLoader
            testId="table-footer-loader"
            customStyle={{
              backgroundColor: colors.gray200,
              margin: fromLg ? "0 24px" : "8px 0"
            }}
            width={toLg ? "calc(100% - 24px)" : "259px"}
            height="36px"
          />
          <PaginationLoader />
        </>
      );
    }

    return (
      <>
        <Autocomplete
          data-testid="rows-per-page-autocomplete"
          fullWidth={toLg}
          disableClearable
          size="small"
          labelLeft={t("Table##rows per page")}
          options={options}
          value={rowsPerPage || "25"}
          onChange={(_, value) => handleRowsPerPageChange(value)}
          customStyle={{
            flexDirection: "row",
            gap: "8px",
            width: toLg ? "calc(100% - 24px)" : "unset"
          }}
          labelCustomStyle={{
            width: toLg ? "101px" : "unset"
          }}
          isOptionEqualToValue={(option, value) => option === value}
          disabled={pagination.totalCount === 0}
        />

        <Pagination
          route={route}
          page={currentPageNumber}
          total={totalPageCount}
          perPage={Number.parseInt(rowsPerPage)}
          onChange={handlePageChange}
        />
      </>
    );
  };

  return (
    <div
      data-testid="table-footer"
      css={css([
        tableFooterStyle(toLg, fromLg, DESKTOP_TABLE_FOOTER_HEIGHT) as {},
        tableFooter
      ])}
    >
      {renderTableFooterContent()}
    </div>
  );
};

export default TableFooter;
